import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { CalendarDays, ChevronDown, MailOpen } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ParticleRain } from '../components/ParticleRain';
import { Sticker } from '../components/Sticker';
import { birthdayConfig } from '../config';
import { assetUrl } from '../data/assets';

function getCountdown(dateString: string) {
  const target = new Date(`${dateString}T00:00:00`);
  const now = new Date();
  const difference = target.getTime() - now.getTime();
  if (difference <= 0) return null;
  return {
    days: Math.floor(difference / 86_400_000),
    hours: Math.floor((difference / 3_600_000) % 24),
    minutes: Math.floor((difference / 60_000) % 60),
    seconds: Math.floor((difference / 1000) % 60),
  };
}

export function HeroSection({ effectsActive }: { effectsActive: boolean }) {
  const [countdown, setCountdown] = useState(() => getCountdown(birthdayConfig.birthdayPerson.birthdayDate));
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 45, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 45, damping: 20 });
  const photoX = useTransform(smoothX, [-0.5, 0.5], [-14, 14]);
  const photoY = useTransform(smoothY, [-0.5, 0.5], [-10, 10]);

  useEffect(() => {
    const timer = window.setInterval(() => setCountdown(getCountdown(birthdayConfig.birthdayPerson.birthdayDate)), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const dateLabel = useMemo(
    () =>
      new Intl.DateTimeFormat('en', { day: 'numeric', month: 'long', year: 'numeric' }).format(
        new Date(`${birthdayConfig.birthdayPerson.birthdayDate}T00:00:00`),
      ),
    [],
  );

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      id="home"
      className="hero-section section-shell"
      onPointerMove={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        mouseX.set((event.clientX - rect.left) / rect.width - 0.5);
        mouseY.set((event.clientY - rect.top) / rect.height - 0.5);
      }}
    >
      <ParticleRain kind="flowers" count={18} active={effectsActive} />
      <ParticleRain kind="sparkles" count={20} active={effectsActive} />
      <div className="hero-aurora" />
      <div className="hero-content page-container">
        <motion.div
          className="hero-copy"
          initial={{ opacity: 0, y: 36 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.18 }}
        >
          <motion.span className="eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}>
            {birthdayConfig.copy.hero.eyebrow}
          </motion.span>
          <h1>
            {birthdayConfig.copy.hero.titlePrefix}
            <span>{birthdayConfig.birthdayPerson.nickname} 💖</span>
          </h1>
          <p className="hero-lead">{birthdayConfig.birthdayPerson.shortMessage}</p>
          <div className="birthday-badge"><CalendarDays size={18} /> {dateLabel}</div>

          {countdown ? (
            <div className="countdown" aria-label="Birthday countdown">
              {Object.entries(countdown).map(([unit, value]) => (
                <div key={unit}><strong>{String(value).padStart(2, '0')}</strong><span>{unit}</span></div>
              ))}
            </div>
          ) : (
            <motion.div className="today-badge" animate={{ scale: [1, 1.04, 1] }} transition={{ repeat: Infinity, duration: 2.4 }}>
              {birthdayConfig.copy.hero.birthdayToday}
            </motion.div>
          )}

          <div className="hero-actions">
            <button className="primary-button" onClick={() => scrollTo('letter')}><MailOpen size={19} /> {birthdayConfig.copy.hero.openLetterButton}</button>
            <button className="secondary-button" onClick={() => scrollTo('memories')}><ChevronDown size={19} /> {birthdayConfig.copy.hero.journeyButton}</button>
          </div>
        </motion.div>

        <motion.div className="hero-visual" style={{ x: photoX, y: photoY }}>
          <div className="balloon balloon-one" aria-hidden="true" />
          <div className="balloon balloon-two" aria-hidden="true" />
          <div className="hero-photo-frame">
            <img
              src={assetUrl('/images/hero-photo.jpg')}
              alt={`${birthdayConfig.birthdayPerson.name} birthday portrait`}
              className="hero-photo"
              loading="eager"
              decoding="async"
              draggable={false}
            />
          </div>
          <Sticker src="/stickers/mochi-heart.svg" alt="Mochi cat holding a heart" className="hero-sticker" delay={0.4} />
          <motion.div className="floating-heart heart-one" animate={{ y: [0, -14, 0], rotate: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 3.2 }}>💗</motion.div>
          <motion.div className="floating-heart heart-two" animate={{ y: [0, -10, 0], rotate: [0, -8, 0] }} transition={{ repeat: Infinity, duration: 4.1 }}>✨</motion.div>
        </motion.div>
      </div>
    </section>
  );
}
